import { useCallback } from 'react';

import { Card } from '@mui/material';
import { PROFILES_ENABLED_PLACEHOLDER } from 'constants/constants';
import { useProfilesManager } from 'hooks/useProfilesManager';

import AcInput from '../../AcInput/AcInput';
import ActionButton from '../../ActionButton/ActionButton';
import CustomizedSwitch from '../../SwitchButton/SwitchButton';
import { Flex } from '../../ui/flex';

import { AuthCardProps } from './PlayerAuthSettings.types';

export const AuthCard = ({
  title,
  state,
  form,
  model,
  on,
  id,
  secret,
  logo,
  inputs,
  appStates,
  children,
  disabled,
  disabledWhenProfilesEnabled
}: AuthCardProps) => {
  const isDisabled = () => {
    return form.values.playersAuthentication?.[id!] === '';
  };

  const { isProfilesEnabled } = useProfilesManager();
  
  const getCurrentInputValue = useCallback((name: string) => 
    name === 'otpGenerateDeeplinkUrl' && isProfilesEnabled
      ? PROFILES_ENABLED_PLACEHOLDER
      : name.split('.').reduce((obj, key) => obj?.[key], form.values),
    [isProfilesEnabled, form.values]
  );

  return (
    <Card
      variant="outlined"
      sx={{
        boxShadow: 0,
        border: 0
      }}
    >
      <Flex justify="between" direction="row">
        <h3
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {logo && (
            <img
              src={`./assets/images/${logo}.png`}
              alt={logo}
              style={{ height: 16, marginRight: 8 }}
            />
          )}
          {title}
        </h3>
        <div className="-mt-2 -mr-6 -mb-2">
          <CustomizedSwitch
            tooltip={
              state &&
              appStates.reduce((acc, cur) => {
                return !cur ? acc + 1 : acc;
              }, 0) >=
                appStates.length - 1
                ? `You can't disable this method because at least one authentication method has to be enabled.`
                : ''
            }
            texts={[]}
            status={state}
            disabled={
              disabled ||
              (isProfilesEnabled && disabledWhenProfilesEnabled) ||
              (state &&
                appStates.reduce((acc, cur) => (!cur ? acc + 1 : acc), 0) >=
                  appStates.length - 1)
            }
            functions={[
              () => {
                form.setFieldValue(`playersAuthentication.${on}`, false);
                form.setFieldValue('isToggleOnOff', true);
                form.submitForm();
              },
              () => {
                form.setFieldValue(`playersAuthentication.${on}`, true);
                form.setFieldValue('isToggleOnOff', true);
                form.submitForm();
              }
            ]}
          />
        </div>
      </Flex>

      <Flex
        style={{
          maxHeight: !state ? '0px' : '400px',
          overflow: 'hidden',
          transition: '0.6s all'
        }}
      >
        {id && (
          <div className="mt-4">
            <AcInput
              header={`${title} ID`}
              headerSize="14"
              name={`playersAuthentication.${id}`}
              value={
                isProfilesEnabled && disabledWhenProfilesEnabled
                  ? PROFILES_ENABLED_PLACEHOLDER
                  : form.values.playersAuthentication[id]
              }
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              error={
                form.touched?.playersAuthentication?.[id] &&
                Boolean(form.errors?.playersAuthentication?.[id])
              }
              helperText={
                form.touched?.playersAuthentication?.[id]
                  ? form.errors?.playersAuthentication?.[id]?.toString()
                  : ''
              }
              disabled={
                disabled || (isProfilesEnabled && disabledWhenProfilesEnabled)
              }
            />
          </div>
        )}
        {inputs &&
          inputs.map((input, index) => (
            <div className="mt-4" key={index}>
              <AcInput
                header={input.header}
                headerSize="14"
                name={input.name}
                value={getCurrentInputValue(input.name)}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={
                  form.touched?.[input.name] &&
                  Boolean(form.errors?.[input.name])
                }
                helperText={
                  form.touched?.[input.name]
                    ? form.errors?.[input.name]?.toString()
                    : ''
                }
                disabled={
                  disabled || (isProfilesEnabled && disabledWhenProfilesEnabled)
                }
              />
            </div>
          ))}
        {children}
        {(model || secret || inputs) && !disabled && (
          <div className="mt-3">
            <ActionButton
              variant="outlined"
              text="Update"
              onClick={form.submitForm}
              disabled={isDisabled()}
            />
          </div>
        )}
      </Flex>
    </Card>
  );
};
